/**
 * Searchable single-select combobox. Replacement for native <select> in
 * forms where the option list is long, dynamic, or benefits from filtering.
 *
 * Visual matches bm-input + a popover list keyed off design tokens.
 * Keyboard: ArrowUp/Down to move, Enter to commit, Esc to close.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from './brand';

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
}

interface Props {
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  name?: string;
}

export function Combobox({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  emptyLabel = 'No matches',
  disabled = false,
  required = false,
  className,
  name,
}: Props) {
  const id = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const lc = query.trim().toLowerCase();
    if (!lc) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(lc) || o.value.toLowerCase().includes(lc),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to `open` transitions; values/options are read on demand
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIdx(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 20);
    return () => clearTimeout(t);
  }, [open]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeIdx is read via the data-attribute set during render
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const node = list.querySelector<HTMLElement>('[data-active="true"]');
    if (!node) return;
    // Manual confine to the list container — scrollIntoView would scroll the
    // document, shifting the popover under the user.
    const top = node.offsetTop;
    const bottom = top + node.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [activeIdx, open]);

  const commit = (opt: ComboboxOption | undefined) => {
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(filtered[activeIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`bm-combobox${className ? ` ${className}` : ''}`}
      data-open={open}
    >
      {/* hidden native field for form submission */}
      {name && <input type="hidden" name={name} value={value} required={required} />}
      <button
        type="button"
        className="bm-input bm-combobox-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span
          className="bm-combobox-value"
          style={{
            color: selected ? 'var(--bm-text)' : 'var(--bm-text-faint)',
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon.chevronDown size={12} />
      </button>

      {open && (
        <div className="bm-combobox-popover" role="dialog">
          <div className="bm-combobox-search">
            <Icon.search size={12} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKey}
              placeholder="Search…"
            />
          </div>
          <div ref={listRef} className="bm-combobox-list" id={`${id}-list`} role="listbox">
            {filtered.length === 0 && <div className="bm-combobox-empty">{emptyLabel}</div>}
            {filtered.map((opt, i) => {
              const isActive = i === activeIdx;
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  data-active={isActive}
                  className="bm-combobox-item"
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => commit(opt)}
                >
                  <span style={{ flex: 1, textAlign: 'left' }}>{opt.label}</span>
                  {opt.hint && (
                    <span className="mono" style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>
                      {opt.hint}
                    </span>
                  )}
                  {isSelected && (
                    <span style={{ color: 'var(--bm-accent)' }}>
                      <Icon.check size={12} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
